'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ConsultationBookingProps {
  currentLanguage: string;
}

const ConsultationBooking = ({ currentLanguage }: ConsultationBookingProps) => {
  const [formData, setFormData] = useState({
    organizationType: '',
    organizationName: '',
    sector: '',
    contactName: '',
    email: '',
    phone: '',
    projectScope: '',
    preferredDate: '',
    message: ''
  });

  const [showSuccess, setShowSuccess] = useState(false);

  const content = {
    en: {
      title: 'Schedule Expert Consultation',
      subtitle: 'Connect with specialized sustainability experts for your sector',
      form: {
        organizationType: {
          label: 'Organization Type',
          options: ['Select Type', 'Government Agency', 'Public Sector Enterprise', 'Private Enterprise', 'NGO/Non-Profit']
        },
        organizationName: {
          label: 'Organization Name',
          placeholder: 'Enter organization name'
        },
        sector: {
          label: 'Primary Sector',
          options: ['Select Sector', 'Utilities', 'Ports & Maritime', 'Railways', 'Infrastructure', 'Energy', 'Water & Sanitation', 'Other']
        },
        contactName: {
          label: 'Contact Person Name',
          placeholder: 'Full name'
        },
        email: {
          label: 'Email Address',
          placeholder: 'contact@organization.com'
        },
        phone: {
          label: 'Phone Number',
          placeholder: '+91 98765 43210'
        },
        projectScope: {
          label: 'Project Scope',
          options: ['Select Scope', 'Feasibility Study', 'Implementation Planning', 'Ongoing Project Support', 'Compliance & Reporting', 'Technology Integration']
        },
        preferredDate: {
          label: 'Preferred Consultation Date',
          placeholder: 'Select date'
        },
        message: {
          label: 'Project Details',
          placeholder: 'Brief description of your sustainability goals and project requirements...'
        },
        submit: 'Schedule Consultation'
      },
      experts: [
        { name: 'Dr. Nupur M', specialty: 'Carbon Credits & ESG', icon: 'UserCircleIcon' },
        { name: 'Infrastructure Team', specialty: 'Utilities & Railways', icon: 'UsersIcon' },
        { name: 'Maritime Experts', specialty: 'Ports & Shipping', icon: 'TruckIcon' },
        { name: 'Environmental Team', specialty: 'Ecosystem Restoration', icon: 'GlobeAltIcon' },
        { name: 'Water/STP Management', specialty: 'Water Treatment & STP Systems', icon: 'BeakerIcon' }
      ],
      success: {
        title: 'Consultation Request Received!',
        message: 'Our team will contact you within 24 hours to confirm your consultation schedule.',
        button: 'Schedule Another'
      }
    },
    hi: {
      title: 'विशेषज्ञ परामर्श शेड्यूल करें',
      subtitle: 'अपने क्षेत्र के लिए विशेष स्थिरता विशेषज्ञों से जुड़ें',
      form: {
        organizationType: {
          label: 'संगठन प्रकार',
          options: ['प्रकार चुनें', 'सरकारी एजेंसी', 'सार्वजनिक क्षेत्र उद्यम', 'निजी उद्यम', 'NGO/गैर-लाभकारी']
        },
        organizationName: {
          label: 'संगठन का नाम',
          placeholder: 'संगठन का नाम दर्ज करें'
        },
        sector: {
          label: 'प्राथमिक क्षेत्र',
          options: ['क्षेत्र चुनें', 'उपयोगिताएं', 'बंदरगाह और समुद्री', 'रेलवे', 'बुनियादी ढांचा', 'ऊर्जा', 'जल और स्वच्छता', 'अन्य']
        },
        contactName: {
          label: 'संपर्क व्यक्ति का नाम',
          placeholder: 'पूरा नाम'
        },
        email: {
          label: 'ईमेल पता',
          placeholder: 'contact@organization.com'
        },
        phone: {
          label: 'फोन नंबर',
          placeholder: '+91 98765 43210'
        },
        projectScope: {
          label: 'परियोजना दायरा',
          options: ['दायरा चुनें', 'व्यवहार्यता अध्ययन', 'कार्यान्वयन योजना', 'चल रही परियोजना समर्थन', 'अनुपालन और रिपोर्टिंग', 'प्रौद्योगिकी एकीकरण']
        },
        preferredDate: {
          label: 'पसंदीदा परामर्श तिथि',
          placeholder: 'तिथि चुनें'
        },
        message: {
          label: 'परियोजना विवरण',
          placeholder: 'अपने स्थिरता लक्ष्यों और परियोजना आवश्यकताओं का संक्षिप्त विवरण...'
        },
        submit: 'परामर्श शेड्यूल करें'
      },
      experts: [
        { name: 'डॉ. नूपुर एम', specialty: 'कार्बन क्रेडिट और ESG', icon: 'UserCircleIcon' },
        { name: 'बुनियादी ढांचा टीम', specialty: 'उपयोगिताएं और रेलवे', icon: 'UsersIcon' },
        { name: 'समुद्री विशेषज्ञ', specialty: 'बंदरगाह और शिपिंग', icon: 'TruckIcon' },
        { name: 'पर्यावरण टीम', specialty: 'पारिस्थितिकी तंत्र बहाली', icon: 'GlobeAltIcon' },
        { name: 'जल/STP प्रबंधन', specialty: 'जल उपचार और STP प्रणाली', icon: 'BeakerIcon' }
      ],
      success: {
        title: 'परामर्श अनुरोध प्राप्त हुआ!',
        message: 'हमारी टीम आपके परामर्श शेड्यूल की पुष्टि करने के लिए 24 घंटे के भीतर आपसे संपर्क करेगी।',
        button: 'एक और शेड्यूल करें'
      }
    },
    ar: {
      title: 'جدولة استشارة الخبراء',
      subtitle: 'تواصل مع خبراء الاستدامة المتخصصين في قطاعك',
      form: {
        organizationType: {
          label: 'نوع المنظمة',
          options: ['اختر النوع', 'وكالة حكومية', 'مؤسسة القطاع العام', 'مؤسسة خاصة', 'منظمة غير حكومية/غير ربحية']
        },
        organizationName: {
          label: 'اسم المنظمة',
          placeholder: 'أدخل اسم المنظمة'
        },
        sector: {
          label: 'القطاع الأساسي',
          options: ['اختر القطاع', 'المرافق', 'الموانئ والبحرية', 'السكك الحديدية', 'البنية التحتية', 'الطاقة', 'المياه والصرف الصحي', 'أخرى']
        },
        contactName: {
          label: 'اسم جهة الاتصال',
          placeholder: 'الاسم الكامل'
        },
        email: {
          label: 'عنوان البريد الإلكتروني',
          placeholder: 'contact@organization.com'
        },
        phone: {
          label: 'رقم الهاتف',
          placeholder: '+91 98765 43210'
        },
        projectScope: {
          label: 'نطاق المشروع',
          options: ['اختر النطاق', 'دراسة الجدوى', 'تخطيط التنفيذ', 'دعم المشروع المستمر', 'الامتثال والتقارير', 'تكامل التكنولوجيا']
        },
        preferredDate: {
          label: 'التاريخ المفضل للاستشارة',
          placeholder: 'اختر التاريخ'
        },
        message: {
          label: 'تفاصيل المشروع',
          placeholder: 'وصف موجز لأهداف الاستدامة ومتطلبات المشروع...'
        },
        submit: 'جدولة الاستشارة'
      },
      experts: [
        { name: 'د. نوبور م', specialty: 'ائتمانات الكربون وESG', icon: 'UserCircleIcon' },
        { name: 'فريق البنية التحتية', specialty: 'المرافق والسكك الحديدية', icon: 'UsersIcon' },
        { name: 'خبراء البحرية', specialty: 'الموانئ والشحن', icon: 'TruckIcon' },
        { name: 'الفريق البيئي', specialty: 'استعادة النظام البيئي', icon: 'GlobeAltIcon' },
        { name: 'إدارة المياه/STP', specialty: 'معالجة المياه وأنظمة STP', icon: 'BeakerIcon' }
      ],
      success: {
        title: 'تم استلام طلب الاستشارة!',
        message: 'سيتصل بك فريقنا خلال 24 ساعة لتأكيد جدول الاستشارة الخاص بك.',
        button: 'جدولة أخرى'
      }
    }
  };

  const currentContent = content[currentLanguage as keyof typeof content] || content.en;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setFormData({
        organizationType: '',
        organizationName: '',
        sector: '',
        contactName: '',
        email: '',
        phone: '',
        projectScope: '',
        preferredDate: '',
        message: ''
      });
    }, 5000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="consultation" className="py-16 md:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary">
            {currentContent.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {currentContent.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Experts */}
          <div className="space-y-4">
            <h3 className="font-heading font-bold text-xl text-text-primary mb-4">
              Our Expert Team
            </h3>
            {currentContent.experts.map((expert, index) => (
              <div 
                key={index}
                className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name={expert.icon as any} size={24} className="text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-text-primary">{expert.name}</div>
                    <div className="text-sm text-text-secondary">{expert.specialty}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            {!showSuccess ? (
              <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-8 shadow-natural">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {currentContent.form.organizationType.label}
                    </label>
                    <select
                      name="organizationType"
                      value={formData.organizationType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {currentContent.form.organizationType.options.map((option, index) => (
                        <option key={index} value={index === 0 ? '' : option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {currentContent.form.organizationName.label}
                    </label>
                    <input
                      type="text"
                      name="organizationName"
                      value={formData.organizationName}
                      onChange={handleChange}
                      placeholder={currentContent.form.organizationName.placeholder}
                      required
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {currentContent.form.sector.label}
                    </label>
                    <select
                      name="sector"
                      value={formData.sector}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {currentContent.form.sector.options.map((option, index) => (
                        <option key={index} value={index === 0 ? '' : option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {currentContent.form.contactName.label}
                    </label>
                    <input
                      type="text"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      placeholder={currentContent.form.contactName.placeholder}
                      required
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {currentContent.form.email.label}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={currentContent.form.email.placeholder}
                      required
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {currentContent.form.phone.label}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={currentContent.form.phone.placeholder}
                      required
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {currentContent.form.projectScope.label}
                    </label>
                    <select
                      name="projectScope"
                      value={formData.projectScope}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {currentContent.form.projectScope.options.map((option, index) => (
                        <option key={index} value={index === 0 ? '' : option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {currentContent.form.preferredDate.label}
                    </label>
                    <input
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {currentContent.form.message.label}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={currentContent.form.message.placeholder}
                      rows={4}
                      required
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full mt-6 px-8 py-4 bg-primary text-primary-foreground font-heading font-semibold text-base rounded-lg hover:bg-primary/90 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic transform hover:scale-102 flex items-center justify-center"
                >
                  {currentContent.form.submit}
                  <Icon name="ArrowRightIcon" size={20} className="ml-2" />
                </button>
              </form>
            ) : (
              <div className="bg-card border border-border rounded-xl p-8 shadow-natural text-center">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="CheckCircleIcon" size={48} className="text-primary" />
                </div>
                <h3 className="font-heading font-bold text-2xl text-text-primary mb-4">
                  {currentContent.success.title}
                </h3>
                <p className="text-text-secondary mb-6">
                  {currentContent.success.message}
                </p>
                <button
                  onClick={() => setShowSuccess(false)}
                  className="px-6 py-2 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 transition-colors"
                >
                  {currentContent.success.button}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConsultationBooking;